import { roundToTwoDecimals } from '@fruitful/fruitils';
import { type TaxFiling } from '@fruitful/types';

import { FEDERAL_TAX_BRACKETS } from './assumptions/federalTaxBrackets';
import { STANDARD_DEDUCTIONS } from './assumptions/standardDeductions';
import { STATE_TAX_RATES } from './assumptions/stateTaxRates';

/**
 * Calculates the top marginal tax rate for gross (ordinary) income.
 * Assumes standard deduction to determine taxable income.
 */
export class MarginalTaxRateCalculator {
    private readonly taxableIncome: number;

    constructor(
        private readonly grossIncome: number,
        private readonly residentialLocation: string,
        private readonly taxFilingStatus: TaxFiling,
    ) {
        // Calculate taxable income by subtracting standard deduction from gross income
        const standardDeduction = STANDARD_DEDUCTIONS[this.taxFilingStatus];
        this.taxableIncome = Math.max(0, this.grossIncome - standardDeduction);
    }

    /**
     * Calculate the marginal tax rate (federal + state)
     * @returns The combined marginal tax rate as a decimal (e.g., 0.35 for 35%)
     */
    public calculate(): number {
        const federalRate = this.calculateFederalMarginalRate();
        const stateRate = this.calculateStateMarginalRate();

        return roundToTwoDecimals(federalRate + stateRate);
    }

    /**
     * Calculate federal marginal tax rate
     * @returns Federal marginal tax rate as a decimal
     */
    private calculateFederalMarginalRate(): number {
        const brackets = FEDERAL_TAX_BRACKETS[this.taxFilingStatus];

        // Find the highest bracket where income exceeds the threshold
        for (let i = brackets.length - 1; i >= 0; i--) {
            const bracket = brackets[i];
            if (bracket && this.taxableIncome > bracket.threshold) {
                return bracket.rate;
            }
        }

        // Fallback to lowest bracket
        const lowestBracket = brackets[0];
        if (!lowestBracket) {
            throw new Error('No tax brackets found');
        }
        return lowestBracket.rate;
    }

    /**
     * Calculate state marginal tax rate
     * @returns State marginal tax rate as a decimal
     */
    private calculateStateMarginalRate(): number {
        const stateCode = this.residentialLocation.toUpperCase();
        const stateRate = STATE_TAX_RATES[stateCode];

        if (stateRate === undefined) {
            // If state not found, default to 0 (no state tax)
            return 0;
        }

        return stateRate;
    }
}
